// ============================================================================
// palette-extract.js — client-side logo palette extraction
// ============================================================================
// Powers the V2 "color chips visibly pop out of the logo and land in the
// pickers" moment (CUSTOM-WEIGHT-DESIGNER-V2-PLAN.md §4 Step 1). Self-contained:
// no framework, no network calls, no theme imports. It ONLY produces colors —
// the separately-built design-profile layer consumes them.
//
// Design (V2 plan §4 + §11):
//   1. Downscale the logo to a small working canvas (≤128px longest edge) —
//      cheap AND denoises JPEG/photo artifacts.
//   2. Read pixels via getImageData, drop near-transparent ones.
//   3. Median-cut color quantization → population-ranked clusters.
//   4. Merge perceptually-identical clusters (CIELAB ΔE) so "distinct colors"
//      actually read as distinct to a human — not two near-identical reds.
//   5. Rank + select primary/secondary/accents, degrading gracefully on the
//      §11 edge cases (photo-like art → low confidence, no guess; monochrome →
//      one color, no fabricated second; decode failure → friendly null result).
//
// STRUCTURE FOR TESTABILITY: everything except the browser-only decode/canvas
// wrapper is a pure function. `analyzePixels`, `medianCut`, `srgbToLab`, and
// `deltaE` are exported and run in plain Node (no DOM). Only `extractPalette`
// needs a browser (createImageBitmap / <canvas> / getImageData).
// ============================================================================

/**
 * @typedef {Object} Swatch
 * @property {string} hex       - "#rrggbb"
 * @property {[number,number,number]} rgb
 * @property {number} population - pixel weight backing this color
 */

/**
 * @typedef {Object} PaletteResult
 * @property {'high'|'low'} confidence
 * @property {Swatch|null} primary   - null when confidence is 'low'
 * @property {Swatch|null} secondary - null when monochrome or low confidence
 * @property {Swatch[]} accents
 * @property {Swatch[]} all          - every distinct color, population-desc
 * @property {Error} [error]         - present only on decode failure
 */

// ---------------------------------------------------------------------------
// Tunable defaults (all overridable via opts)
// ---------------------------------------------------------------------------
const DEFAULTS = {
  maxEdge: 128,          // longest-edge px of the analysis canvas (analysis-only)
  maxColors: 8,          // median-cut target bucket count
  alphaThreshold: 200,   // 0-255; below this alpha is ignored — high, so the semi-
                         // transparent anti-aliased RIM of a cut-out logo (muddy
                         // colour+background blends) never enters the palette
  rimSolidityMax: 0.12,  // a colour whose pixels almost all vanish under a 1px erosion has no
                         // interior — it is a fringe around something else, not a region
  rimNeighborMin: 0.5,   // …and at least this much of its non-background boundary faces ONE
                         // other colour, i.e. it wraps that colour rather than the backdrop
  rimLineTol: 30,        // …and it sits this close to the host→black / host→white line, i.e.
                         // it is that colour darkened or lightened (premultiplied-alpha edge).
                         // Generous on purpose: the two spatial tests above already carry the
                         // weight, so this only has to exclude genuinely different hues.

  // ── the JPEG SEAM between two inks (Ryan 2026-07-28) — see findRimArtifacts branch B ──
  seamSolidityMax: 0.02, // it erodes to NOTHING. Far tighter than rimSolidityMax because this
                         // branch's colour test is far looser, so the spatial evidence has to be
                         // absolute: measured, a seam is 0.000 and the softest real colour is 0.27.
  seamBgMax: 0.15,       // …and it lives BETWEEN inks, so almost none of its boundary faces the
                         // background. This is what protects a thin element on transparency, whose
                         // whole outer side is background.
  seamLineTol: 44,       // …and it sits this close to the segment between two colours that both
                         // out-weigh it. Loose ON PURPOSE, and it is the tolerance dropBlends
                         // cannot use: JPEG chroma subsampling and ringing bend a seam well off a
                         // straight interpolation (the Denver badge's two seams measure 21.9 and
                         // 38.0 off it). What makes loose safe here is that nothing with an
                         // interior can reach this branch at all. A genuine keyline is CONTRASTING
                         // by definition — that is what a keyline is for — so it lands hundreds of
                         // units off any such segment (white between navy and orange: 261).
  seamMaxRatio: 0.33,    // …and it is much rarer than the colours it sits between. Same number and
                         // the same reasoning as blendMaxRatio.
  blendTol: 16,          // RGB distance under which a colour lying BETWEEN two more-
                         // dominant colours is treated as an anti-alias blend (red+white→
                         // pink, navy+white→periwinkle) and dropped (0 disables). RGB,
                         // not Lab: pixel blending is linear in sRGB, curved in Lab.
  blendMaxRatio: 0.33,   // only drop a suspected blend if its population is below this ×
                         // the LARGER endpoint. An anti-alias seam is small vs the bigger
                         // region; a real collinear colour (orange between red & yellow) or
                         // a gradient mid-tone is comparably populous and KEPT — a gradient
                         // tone is fine as a 3rd accent, the extremes still lead (see the
                         // max-contrast secondary below). (Max, not min: min gives too low a
                         // bar when one endpoint is a small region.)
  removedBg: null,       // [r,g,b] of a COLOUR background the caller keyed out, or null.
                         // Used as a phantom blend endpoint (kills white↔bg anti-alias
                         // greys the removed bg would otherwise leave orphaned) and to
                         // drop residual near-bg colours. NEVER pass for a white key —
                         // white is usually a real logo colour there.
  bgResidualTol: 72,     // RGB distance from removedBg under which a leftover cluster is
                         // treated as un-keyed background residue and dropped

  /* ⛔ THE QUANTIZER'S ANSWER MUST BE A COLOUR THE PIXELS ACTUALLY WEAR (WD 430). A median-cut
     bucket reports its AVERAGE, and where two inks meet the anti-aliased fringe gets a bucket of
     its own whose average is a colour NEITHER ink has. Ryan found one by eye - *"that brown
     color... I don't even see it on the logo itself"* - and he was right: webster's #754514 is in
     zero pixels, the midpoint of its navy and its gold.
       ⭐ THE TEST IS THE MECHANISM ITSELF, WHICH IS AN OVERCLAIM. A bucket's average is reported
     as the colour of every pixel IN that bucket, but on a fringe bucket only the thin seam is
     actually that colour. So compare what a swatch CLAIMS (its population share) against what
     wears it (the share of pixels within `phantomSupportTol`). A real ink supports its own claim;
     CSI's invented cream claims 30.1% of the mark and 0.01% of its pixels wear it.
       ⚠ THIS REPLACED A FIRST CUT THAT MEASURED ΔE TO THE NEAREST CENSUS ROW, and the reason is
     worth keeping: a census row is a MERGED representative, so on a gradient-heavy mark (angry
     panda) a perfectly real mid-tone sits far from every row and read as a phantom. The pixels
     wear it; the census simply had no row for it. Support asks the pixels directly and does not
     inherit the census's resolution.
       ⚠ AND THE BAR IS MEASURED, NOT CHOSEN. Over the captured corpus the ratio is bimodal: real
     inks cluster at 0.6-1.1 and the phantoms sit at 0.00-0.24, with almost nothing between. */
  phantomSupportTol: 10, // ΔE within which a pixel counts as WEARING a swatch (the census's own bar)
  phantomMinSupport: 0.25, // supported/claimed under this = a colour the mark does not wear. 0 disables
  /* ⛔ ...AND ONLY IF ANYONE CAN SEE IT. Repairing a phantom is not free. `bears`' #944857 claims
     0.9% of the palette and 0.07% of its pixels wear it - an unmistakable phantom - and removing it
     cost that mark its ENTIRE band structure. `analyzeStructure` labels every pixel against
     `palette.all`, and `outerBgMin` and its neighbours are constants calibrated against that
     labelling: the fragility WD 424 quarantined, which a palette change reopens by the back door.
     So a phantom too small to reach any surface is left where it is; the two ROLES are exempt
     because a role is shown whatever its share.
       ⚠ THE BAR IS BELOW THE CHIP THRESHOLD ON PURPOSE, and this is the part to read before moving
     it. `shownLogoTones` shows an accent at 3% of the PALETTE, but a band is a share of the
     STRUCTURE's foreground, measured on a different canvas (220px vs 128px) against a different
     label set - so a swatch under 3% of the palette can still be a BAND, and two of the five
     phantom bands WD 429b counted are exactly that: polkeagles' #E8D5A9 at 2.3% and
     www-svsucardinals' #8C7D89 at 2.9%. A 3% bar left both of them in place.
       The number is therefore EMPIRICAL and bounded on both sides: it must catch 2.3%, and it must
     not catch the 0.9% that cost a structure. Measured over the captured corpus, 0.02 catches both
     phantom bands, costs ONE extra mark and ONE extra plate, and still loses no structure.
     ⚠ That gap (0.9 ↔ 2.3) was measured over 56 marks, not 132. A fuller capture may narrow it;
     re-measure with `probe-structure-diff.mjs` before trusting it, and never move it on a hunch. */
  phantomMinShare: 0.02, // a phantom under this share of the palette, and not a role, is left alone
  minDeltaE: 12,         // CIELAB ΔE below which two colors count as "the same"
  minChroma: 12,         // CIELAB chroma at/above which a color is "chromatic/meaningful"
  minColorFraction: 0.03,// a colour must own at least this pixel share to be picked as
                         // primary/secondary — stops a 1%-share muddy chromatic blend
                         // from being promoted to secondary over the population floor
  allowNeutralSecondary: false, // when no CHROMATIC secondary exists, may a prominent
                         // NEUTRAL (white/black) fill the secondary slot? Off for the raw
                         // function (a red-on-white-bg logo shouldn't call white a brand
                         // colour). WD turns it ON because the background is always keyed
                         // out before extraction, so any remaining white is foreground.
  ignoreBackground: true,// prefer chromatic colors for primary (de-emphasize white/black bg)
  lowConfidenceDominance: 0.15, // if the top color owns less than this share...
  lowConfidenceClusterCount: 8, // ...AND there are at least this many significant colors → photo-like

  /* ⛔ A SMEAR IS NOT A PLATE COLOUR (WD 481, Ryan on the Grand Hyatt 35 lb maroon plate: *"I'd rather
     it went"*). See `smearSwatches`. Measured on a SECOND read of the art at up to this edge, because
     on the 128px canvas a 1-px smear and a real thin line are the same pixel. 0 disables. */
  smearEdge: 512,       // 512 not 768: the same 6 plates, worst mark 0.2 s instead of 1.4 s (desktop)
  smearRampMin: 0.8,     // share of the tone's pixels that sit ON A RAMP between two other colours
  smearSolidMax: 0.1,    // share that survives a 1-px erosion at full size: a smear has no inside
  smearMinScale: 3,      // the full-size read must be at least this many times the 128 canvas,
                         // or there is no extra resolution to tell a smear from a thin line with
};

// ===========================================================================
// COLOR MATH (pure — sRGB → XYZ → CIELAB + ΔE) — exported for tests
// ===========================================================================

/** sRGB channel (0-255) → linear light (0-1). */
function srgbChannelToLinear(c) {
  const cs = c / 255;
  return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

/** sRGB [r,g,b] (0-255) → CIE XYZ (D65). */
function srgbToXyz([r, g, b]) {
  const R = srgbChannelToLinear(r);
  const G = srgbChannelToLinear(g);
  const B = srgbChannelToLinear(b);
  // sRGB D65 matrix (IEC 61966-2-1)
  return [
    R * 0.4124564 + G * 0.3575761 + B * 0.1804375,
    R * 0.2126729 + G * 0.7151522 + B * 0.0721750,
    R * 0.0193339 + G * 0.1191920 + B * 0.9503041,
  ];
}

/**
 * sRGB [r,g,b] (0-255) → CIELAB [L,a,b] (D65 reference white).
 * @param {[number,number,number]} rgb
 * @returns {[number,number,number]}
 */
export function srgbToLab(rgb) {
  let [x, y, z] = srgbToXyz(rgb);
  // Normalize by D65 reference white
  x /= 0.95047;
  y /= 1.0;
  z /= 1.08883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/**
 * Perceptual color difference (CIE76 ΔE — Euclidean distance in CIELAB).
 * ΔE(a,a)===0; roughly, <2.3 is imperceptible, >10 reads as clearly distinct.
 * @param {[number,number,number]} lab1
 * @param {[number,number,number]} lab2
 * @returns {number}
 */
export function deltaE(lab1, lab2) {
  const dl = lab1[0] - lab2[0];
  const da = lab1[1] - lab2[1];
  const db = lab1[2] - lab2[2];
  return Math.sqrt(dl * dl + da * da + db * db);
}

/** CIELAB chroma = sqrt(a² + b²). 0 for any neutral gray/black/white. */
function labChroma(lab) {
  return Math.sqrt(lab[1] * lab[1] + lab[2] * lab[2]);
}

/** [r,g,b] → "#rrggbb". */
export function rgbToHex([r, g, b]) {
  const h = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

// ===========================================================================
// MEDIAN-CUT QUANTIZER (pure) — exported for tests
// ===========================================================================

/** Longest-spread channel of a bucket → { channel: 0|1|2, range }. */
function widestChannel(bucket) {
  let rMin = 255, rMax = 0, gMin = 255, gMax = 0, bMin = 255, bMax = 0;
  for (const p of bucket) {
    if (p[0] < rMin) rMin = p[0];
    if (p[0] > rMax) rMax = p[0];
    if (p[1] < gMin) gMin = p[1];
    if (p[1] > gMax) gMax = p[1];
    if (p[2] < bMin) bMin = p[2];
    if (p[2] > bMax) bMax = p[2];
  }
  const rr = rMax - rMin;
  const gr = gMax - gMin;
  const br = bMax - bMin;
  if (rr >= gr && rr >= br) return { channel: 0, range: rr };
  if (gr >= rr && gr >= br) return { channel: 1, range: gr };
  return { channel: 2, range: br };
}

/** Weighted average color of a bucket → { rgb, hex, population }. */
function bucketToColor(bucket) {
  let r = 0, g = 0, b = 0, pop = 0;
  for (const p of bucket) {
    const w = p[3] || 1;
    r += p[0] * w;
    g += p[1] * w;
    b += p[2] * w;
    pop += w;
  }
  const rgb = [Math.round(r / pop), Math.round(g / pop), Math.round(b / pop)];
  return { rgb, hex: rgbToHex(rgb), population: pop };
}

/**
 * Median-cut color quantization. Repeatedly splits the bucket with the widest
 * color spread (along its longest channel, at its median) until `maxColors`
 * buckets exist or nothing splittable remains.
 *
 * @param {Array<[number,number,number]|[number,number,number,number]>} pixels
 *        Each pixel is [r,g,b] or [r,g,b,weight] (weight defaults to 1).
 * @param {number} [maxColors=8]
 * @returns {Array<{rgb:[number,number,number],hex:string,population:number}>}
 *          population-descending
 */
export function medianCut(pixels, maxColors = 8) {
  if (!pixels || pixels.length === 0) return [];

  // Histogram-collapse identical colors into weighted entries first. This
  // dedupes the (often huge) pixel list into unique [r,g,b,weight] rows, which
  // makes populations exact and the split loop dramatically cheaper. A bucket
  // of N identical pixels becomes one un-splittable entry — so a 2-color image
  // yields exactly 2 clusters, not maxColors near-duplicates.
  const hist = new Map();
  for (const p of pixels) {
    const w = p[3] || 1;
    const key = ((p[0] & 255) << 16) | ((p[1] & 255) << 8) | (p[2] & 255);
    const cur = hist.get(key);
    if (cur) cur[3] += w;
    else hist.set(key, [p[0], p[1], p[2], w]);
  }
  let buckets = [Array.from(hist.values())];

  while (buckets.length < maxColors) {
    // Pick the splittable bucket with the greatest channel range.
    let target = -1;
    let targetRange = 0;
    let targetChannel = 0;
    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i].length < 2) continue;
      const { channel, range } = widestChannel(buckets[i]);
      if (range > targetRange) {
        targetRange = range;
        target = i;
        targetChannel = channel;
      }
    }
    if (target === -1 || targetRange === 0) break; // fully quantized

    const bucket = buckets[target];
    bucket.sort((a, b) => a[targetChannel] - b[targetChannel]);
    const mid = bucket.length >> 1;
    buckets.splice(target, 1, bucket.slice(0, mid), bucket.slice(mid));
  }

  return buckets.map(bucketToColor).sort((a, b) => b.population - a.population);
}

// ===========================================================================
// THE RASTER'S OWN TONE CENSUS - what the pixels WEAR, not what a quantizer
// averaged (WD 424, moved here from logo-structure.js by WD 430)
// ===========================================================================
// ⛔ IT LIVES IN THIS FILE NOW BECAUSE BOTH SIDES NEED IT AND THEY MUST NOT DISAGREE. WD 424
// built it for `analyzeStructure`, to stop a deleted ink's pixels being reported as the survivor
// that absorbed them. WD 430 needs the same measurement one step EARLIER - to ask the palette
// whether its own swatches exist - and a second copy of "which tones does this mark wear" is
// exactly the shape of bug `shownLogoTones` was written to end. One census; the structure module
// imports it.
//
// ⚠ DEPENDS ON NOTHING BUT THE PIXEL LIST, which is what lets it run BEFORE the labelling pass
// it feeds, and before any palette exists at all.

/**
 * Which tones the mark actually wears, measured from pixels.
 *
 * 16-step RGB bins, kept at >=0.15% of the ink, then merged within a small ΔE so one ink split
 * across neighbouring bins reads as one row. ⭐ THE MERGE KEEPS THE MOST POPULOUS BIN'S COLOUR
 * rather than re-averaging, so a row's hex is always close to a colour real pixels wear - never a
 * centroid between two inks, which is the failure mode this whole file's `medianCut` has and the
 * reason this exists.
 *
 * @param {Array<[number,number,number]>} pixels opaque pixels
 * @returns {Array<{hex:string, rgb:[number,number,number], c:number}>} population-descending
 */
export function censusTones(pixels) {
  const bins = new Map();
  let fg = 0;
  for (const px of pixels) {
    const r = px[0], g = px[1], b = px[2];
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    let cell = bins.get(key);
    if (!cell) { cell = { c: 0, r: 0, g: 0, b: 0 }; bins.set(key, cell); }
    cell.c += 1; cell.r += r; cell.g += g; cell.b += b;
    fg += 1;
  }
  const minPx = Math.max(24, fg * 0.0015);
  const kept = [...bins.values()].filter((x) => x.c >= minPx)
    .map((x) => ({ c: x.c, rgb: [x.r / x.c, x.g / x.c, x.b / x.c] }))
    .sort((a, b) => b.c - a.c);
  const rows = [];
  for (const x of kept) {
    const lab = srgbToLab(x.rgb);
    const hit = rows.find((y) => deltaE(y.lab, lab) < 10);
    if (hit) { hit.c += x.c; continue; } // mass joins the row; the ROW KEEPS ITS OWN COLOUR
    if (rows.length >= 24) continue;
    rows.push({ lab, rgb: x.rgb, c: x.c });
  }
  const hx = (v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
  return rows.map((y) => ({
    hex: `#${hx(y.rgb[0])}${hx(y.rgb[1])}${hx(y.rgb[2])}`.toUpperCase(),
    rgb: [y.rgb[0], y.rgb[1], y.rgb[2]],
    c: y.c,
  }));
}

const SEAM_TOL = 16;         // RGB distance from the a-b line (the same number as `blendTol`)
const SEAM_MAX_RATIO = 0.33; // ...and it is this much rarer than BOTH endpoints (`blendMaxRatio`)

/**
 * Drop ANTI-ALIAS SEAMS from a census - the one thing the median-cut path's `dropBlends` does for
 * the palette, done colorimetrically over measured rows.
 *
 * A tone is a seam when it sits within `SEAM_TOL` of the straight line between two tones that BOTH
 * out-weigh it by at least 1/`SEAM_MAX_RATIO`, at a parameter strictly between the ends.
 *
 * ⭐ **Webster's navy cannot be caught by this and that is the proof it is safe**: at 39% of the
 * ink nothing out-weighs it, so the ratio guard never opens. What it does catch is webster's
 * #090828 (9%, between the navy and the transparent edge), which has no business being a layer.
 *
 * ⚠ RGB, not Lab: pixel blending is linear in sRGB and curved in Lab - the same reason `blendTol`
 * is an RGB distance.
 */
export function dropSeamTones(inks) {
  if (inks.length < 3) return inks; // two tones cannot sandwich a third
  const kept = inks.filter((t) => {
    for (const a of inks) {
      if (a === t || a.c * SEAM_MAX_RATIO <= t.c) continue;
      for (const b of inks) {
        if (b === t || b === a || b.c * SEAM_MAX_RATIO <= t.c) continue;
        const { dist, t: u } = pointToSegment(t.rgb, a.rgb, b.rgb);
        if (dist < SEAM_TOL && u > 0.1 && u < 0.9) return false;
      }
    }
    return true;
  });
  return kept.length >= 2 ? kept : inks; // never strip the basis to nothing
}

/**
 * Adopt an EXISTING LIST'S SPELLING of a tone it already knows about.
 *
 * The census decides WHICH tones exist and how much of the mark each owns - that part is not
 * negotiable, it is why the census exists. But where a census row and a known swatch are the same
 * colour (ΔE < `tol`, the extractor's own "same colour" bar), the swatch's hex is the better
 * label: it is the spelling already on the buyer's chips, and a bin average would differ from it
 * by a digit or two for no reason. A row with NO match keeps its own honest hex - that is the ink
 * the other list forgot, and reporting it is the entire point.
 *
 * ⛔ WITHOUT THIS, A REBUILD RENAMES EVERY TONE IT DID NOT NEED TO TOUCH. Measured on the corpus:
 * the census rebuild moved 81 plate colours, and 41 of them were #FFFFFF becoming #FEFEFE on a
 * mark where nothing about the picture changed.
 */
export function adoptSpelling(inks, swatches, tol = 12) {
  const sl = (swatches || []).filter((sw) => sw && sw.rgb).map((sw) => ({ sw, lab: srgbToLab(sw.rgb) }));
  const used = new Set();
  return inks.map((t) => {
    const lab = srgbToLab(t.rgb);
    let best = null, bestD = tol;
    for (const cand of sl) {
      if (used.has(cand.sw)) continue; // one swatch cannot spell two different inks
      const d = deltaE(lab, cand.lab);
      if (d < bestD) { bestD = d; best = cand; }
    }
    if (!best) return t;
    used.add(best.sw);
    return { ...t, hex: best.sw.hex, rgb: best.sw.rgb };
  });
}

// ===========================================================================
// SELECTION (pure) — quantize → merge → rank → degrade. Exported for tests.
// ===========================================================================

/** Strip internal fields, round population, expose the public swatch shape. */
function toSwatch(c) {
  return { hex: c.hex, rgb: c.rgb, population: Math.round(c.population) };
}

/**
 * Merge perceptually-identical clusters (ΔE < minDeltaE) into distinct colors,
 * accumulating population onto the more-populous representative.
 */
function mergeDistinct(clusters, minDeltaE) {
  const distinct = [];
  for (const c of clusters) {
    let merged = false;
    for (const d of distinct) {
      if (deltaE(c.lab, d.lab) < minDeltaE) {
        d.population += c.population;
        merged = true;
        break;
      }
    }
    if (!merged) distinct.push({ ...c });
  }
  distinct.sort((a, b) => b.population - a.population);
  return distinct;
}

/** Perpendicular distance from point `p` to the segment `a`–`b` (any 3-vector),
 *  plus the clamped projection parameter `t` (0 at a, 1 at b). */
function pointToSegment(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const l2 = dx * dx + dy * dy + dz * dz;
  if (l2 === 0) return { dist: Infinity, t: 0 };
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy + (p[2] - a[2]) * dz) / l2;
  t = Math.max(0, Math.min(1, t));
  const qx = a[0] + t * dx, qy = a[1] + t * dy, qz = a[2] + t * dz;
  return { dist: Math.sqrt((p[0] - qx) ** 2 + (p[1] - qy) ** 2 + (p[2] - qz) ** 2), t };
}

/**
 * Drop anti-alias BLEND clusters: a colour that lies ON the RGB segment between two
 * MORE-dominant colours (red+white→pink, navy+white→periwinkle, navy+red→purple) is
 * an edge blend, not a brand colour. Tested in RGB because pixel blending is linear
 * there (it curves in Lab). This is colour geometry, not a spatial test, so it
 * removes seams WITHOUT touching intentional thin colours — a white outline isn't on
 * the navy↔red line, so it stays. Three guards prevent eating a real 3rd colour:
 *   - the projection falls genuinely BETWEEN the endpoints (t in 0.15–0.85), not near
 *     an end (which would just be a shade of one colour);
 *   - both endpoints out-weigh the candidate; and
 *   - the candidate is MUCH rarer than the smaller endpoint (blendMaxRatio) — a real
 *     collinear colour (orange between red & yellow) is comparably populous, a seam
 *     isn't.
 */
function dropBlends(distinct, tol, maxRatio, anchors = []) {
  if (tol <= 0 || distinct.length < 2) return distinct;
  // Endpoints = the real clusters PLUS any caller anchors (e.g. a removed background
  // colour). Anchors are always-valid endpoints, never candidates for dropping.
  const endpoints = distinct.concat(anchors.map((rgb) => ({ rgb, population: Infinity, anchor: true })));
  return distinct.filter((c) => {
    for (const a of endpoints) {
      if (a === c || a.population <= c.population) continue;
      for (const b of endpoints) {
        if (b === c || b === a || b.population <= c.population) continue;
        if (c.population >= Math.max(a.population, b.population) * maxRatio) continue;
        const { dist, t } = pointToSegment(c.rgb, a.rgb, b.rgb);
        // The population guard above is the real protector of a genuine collinear
        // colour, so the between-window can be generous: catch blends near the
        // endpoints too (navy+red→indigo sits at t≈0.14). Anchor pairs (a removed
        // background) go a touch wider still.
        const anchored = a.anchor || b.anchor;
        const tMin = anchored ? 0.05 : 0.1;
        const tMax = anchored ? 0.95 : 0.9;
        if (dist < tol && t > tMin && t < tMax) return false; // it's a blend → drop
      }
    }
    return true;
  });
}

/**
 * Find RIM ARTIFACT colours: ones that exist only as a fringe around another colour, never as a
 * region of their own (Ryan 2026-07-25).
 *
 * The case that forced this: a logo exported with premultiplied alpha carries a DARKENED copy of
 * each colour along every soft edge. On Garage Gym Reviews the red barbell and diamonds are one
 * colour, #F12735, but a 1–2px rim of #D22E3A wraps all of them — 8% of the red pixels, enough to
 * clear the significance floor, so it was promoted to a brand colour that exists nowhere in the art.
 * Purely colorimetric blend-detection can't catch it: it needs two endpoints that both out-weigh the
 * candidate, and the other endpoint here is BLACK, which isn't a cluster. Measured, that rim sits
 * 16.25 from the red→black line against a tolerance of 16 — far too marginal to fix by tuning.
 *
 * The prior decision this revisits is real and is quoted in extractPalette: a spatial filter alone
 * "can't tell a 1px anti-alias seam from a 1px intentional outline, so it eats real thin colours."
 * That is why thinness alone is NOT the test. All three must hold:
 *   1. SPATIAL — it erodes to nothing, so it has no interior of its own (a fringe, not a region);
 *   2. ENCLOSED — its boundary is dominated by ONE other, more populous colour, rather than by the
 *      background (a white wordmark on transparency fails here — its neighbour is mostly nothing);
 *   3. COLORIMETRIC — it lies on the line from that neighbour toward black or white, i.e. it is that
 *      colour darkened or lightened. A genuine thin gold keyline around navy is not on the navy→black
 *      line, so it survives all the same.
 * Only the conjunction fires, which is what keeps intentional thin colours safe.
 *
 * Pure — takes ImageData, so it is node-testable against synthetic images.
 * @returns {Array<number>} indices into `colors` that are artifacts.
 */
export function findRimArtifacts(imageData, colors, opts = {}) {
  const o = { ...DEFAULTS, ...opts };
  if (!imageData || !imageData.data || !Array.isArray(colors) || colors.length < 2) return [];
  const { data, width: w, height: h } = imageData;
  const n = w * h;
  const BG = -1;
  const rgbs = colors.map((c) => c.rgb || c);
  // Nearest-colour label per opaque pixel.
  const label = new Int16Array(n).fill(BG);
  const pop = new Array(colors.length).fill(0);
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    if (data[i + 3] < o.alphaThreshold) continue;
    let best = 0, bestD = Infinity;
    for (let k = 0; k < rgbs.length; k++) {
      const t = rgbs[k];
      const d = (data[i] - t[0]) ** 2 + (data[i + 1] - t[1]) ** 2 + (data[i + 2] - t[2]) ** 2;
      if (d < bestD) { bestD = d; best = k; }
    }
    label[p] = best;
    pop[best] += 1;
  }
  const at = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? BG : label[y * w + x]);
  const core = new Array(colors.length).fill(0);
  const nbr = colors.map(() => new Map());
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const k = label[y * w + x];
      if (k === BG) continue;
      const ns = [at(x - 1, y), at(x + 1, y), at(x, y - 1), at(x, y + 1)];
      let same = 0;
      for (const v of ns) {
        if (v === k) { same += 1; continue; }
        nbr[k].set(v, (nbr[k].get(v) || 0) + 1);
      }
      if (same === 4) core[k] += 1; // survives a 1px erosion → it has an interior
    }
  }
  const out = [];
  for (let k = 0; k < colors.length; k++) {
    if (!pop[k]) continue;
    const solidity = core[k] / pop[k];
    let dom = -1, domN = 0, tot = 0, bg = 0;
    for (const [v, c] of nbr[k]) {
      if (v === BG) { bg += c; continue; }
      tot += c;
      if (c > domN) { domN = c; dom = v; }
    }
    // ── BRANCH A — the PREMULTIPLIED-ALPHA RIM: one host, darkened or lightened ────────────────
    if (solidity <= o.rimSolidityMax && dom >= 0 && tot && domN / tot >= o.rimNeighborMin && pop[dom] > pop[k]) {
      const host = rgbs[dom];
      const onLine = [[0, 0, 0], [255, 255, 255]].some((end) => pointToSegment(rgbs[k], host, end).dist < o.rimLineTol);
      if (onLine) { out.push(k); continue; }
    }
    // ── BRANCH B — the JPEG SEAM between two inks (Ryan 2026-07-28) ────────────────────────────
    // The Denver Broncos badge extracted #4D313A at 3.9% and #CD917C at 1.9%, and Ryan could not
    // find either anywhere in the artwork. Mapped, they are the JPEG's edge seam: a hairline
    // tracing every letter of the wordmark, the horse, and both rings. Solidity 0.000 — not one
    // pixel survives a 1px erosion, against 0.760 / 0.590 / 0.274 for the three real colours.
    //
    // Both existing filters miss it, and each for a reason that is structural rather than tunable:
    //   · dropBlends is purely colorimetric and needs the seam ON the navy→orange line. JPEG chroma
    //     subsampling plus ringing put it 21.9 away against a tolerance of 16.
    //   · Branch A needs ≥50% of the boundary facing ONE host; it gets 40%. Because the seam is
    //     everywhere, its boundary is split between orange, navy, white AND its own sibling seam
    //     colour — the very ubiquity that makes it obviously an artefact is what stops it looking
    //     enclosed. It then fails A's colour test too (45.5 off orange→black), and it always will:
    //     A models one colour DARKENED, and this is the midpoint of two DIFFERENT colours.
    //
    // So branch B asks the question that actually describes it: is this the mortar BETWEEN two
    // inks? Three things must all hold, and the spatial two are what license the loose third.
    //   1. It erodes to nothing — no interior anywhere, so it is a fringe and not a region.
    //   2. It essentially never faces the background — it lives between inks. A thin element sitting
    //      on transparency has its whole outer side against background and fails here.
    //   3. It lies between two OTHER extracted colours that both out-weigh it, and is much rarer
    //      than they are.
    // The case this must never eat is a 1px INTERNAL KEYLINE that appears nowhere else in the mark
    // — that passes 1 and 2. Test 3 is what saves it, decisively rather than marginally: a keyline
    // exists to CONTRAST with what it separates, so it sits nowhere near the segment joining them.
    // White between navy and orange measures 261 against this tolerance of 44.
    if (solidity > o.seamSolidityMax) continue;
    if (!tot) continue;
    if (bg / (bg + tot) > o.seamBgMax) continue;
    const between = (() => {
      for (let a = 0; a < colors.length; a++) {
        if (a === k || pop[a] <= pop[k]) continue;
        for (let b = a + 1; b < colors.length; b++) {
          if (b === k || pop[b] <= pop[k]) continue;
          if (pop[k] >= Math.min(pop[a], pop[b]) * o.seamMaxRatio) continue;
          // At least one endpoint must be somewhere this colour ACTUALLY TOUCHES. Without it the
          // test is pure colour geometry again — a looser dropBlends — and could pair two colours
          // the candidate never sits between.
          if (!nbr[k].get(a) && !nbr[k].get(b)) continue;
          const { dist, t } = pointToSegment(rgbs[k], rgbs[a], rgbs[b]);
          if (dist < o.seamLineTol && t > 0.15 && t < 0.85) return true;
        }
      }
      return false;
    })();
    if (between) out.push(k);
  }
  return out;
}

/**
 * Quantize a flat pixel list and select a palette. Pure — no DOM. This is the
 * full analysis pipeline minus image decode, so the ranking + edge-case
 * behavior is testable in Node.
 *
 * @param {Array<[number,number,number]|[number,number,number,number]>} pixels
 *        Opaque pixels only ([r,g,b] or [r,g,b,weight]).
 * @param {Partial<typeof DEFAULTS>} [opts]
 * @returns {PaletteResult}
 */
export function analyzePixels(pixels, opts = {}, basis = null) {
  const o = { ...DEFAULTS, ...opts };
  const empty = { confidence: 'low', primary: null, secondary: null, accents: [], all: [] };
  if (!pixels || pixels.length === 0) return empty;

  /* ── DECLARED-INK ANCHORS (Ryan 2026-08-23, Abbey Athletics) ──────────────────────────────────
   * A vector source DECLARES its inks, and discovering them from rasterized pixels instead is
   * what made the same SVG extract 3 tones on one machine and 4 on another: Abbey's white
   * (#FEFEFE) and gray (#B5B6B3) sit ~ΔE 19 apart, `mergeDistinct` fuses or keeps them depending
   * on anti-aliasing noise that differs per browser, and the whole band structure diverges
   * downstream. When the caller hands in the declared inks, pixels are ASSIGNED to them —
   * deterministic on every machine, and exactly the artwork's own truth. No merge pass (declared
   * inks are distinct by definition) and no blend-drop (every pixel snaps to a declared ink, so
   * blends never surface as clusters). Anchors that catch almost nothing are dropped — a `fill`
   * attribute on an invisible path is not a tone. Raster sources are untouched. */
  let clusters;
  if (Array.isArray(o.paletteAnchors) && o.paletteAnchors.length) {
    const ANCHOR_TOL2 = 60 * 60;
    const acc = o.paletteAnchors.map((rgb) => ({
      rgb: [rgb[0], rgb[1], rgb[2]],
      hex: `#${[rgb[0], rgb[1], rgb[2]].map((v) => Math.max(0, Math.min(255, v | 0)).toString(16).padStart(2, '0')).join('').toUpperCase()}`,
      population: 0,
    }));
    let assigned = 0;
    for (const px of pixels) {
      let best = -1, bd = ANCHOR_TOL2;
      for (let k = 0; k < acc.length; k++) {
        const t = acc[k].rgb;
        const d = (px[0] - t[0]) ** 2 + (px[1] - t[1]) ** 2 + (px[2] - t[2]) ** 2;
        if (d <= bd) { bd = d; best = k; }
      }
      if (best >= 0) { acc[best].population += 1; assigned += 1; }
    }
    // The declared inks must actually cover the raster — a stylesheet-driven or gradient-heavy
    // SVG declares colours its pixels don't wear. Under 70% coverage, fall back to discovery.
    if (assigned >= pixels.length * 0.7) {
      clusters = acc
        .filter((c) => c.population >= pixels.length * 0.002) // an invisible path's ink is not a tone
        .sort((a, b) => b.population - a.population)
        .map((c) => { const lab = srgbToLab(c.rgb); return { ...c, lab, chroma: labChroma(lab) }; });
    }
  }
  const anchored = !!(clusters && clusters.length);
  if (!anchored) {
    // Quantize, then annotate with CIELAB + chroma.
    const quant = medianCut(pixels, o.maxColors);
    /* ⛔ ...UNLESS THE CALLER HANDS IN A MEASURED BASIS (WD 430). `analyzeImageData` re-enters here
       with a repaired candidate list when the quantizer's answer turned out to contain a colour no
       pixel of the mark wears - see the long note on the gate there. The candidates change; nothing
       below this line does, which is the point: merge, blend-drop, the background filter and the
       role picker are the same code for either basis. Each entry is {hex, rgb, population}, already
       spelled and capped by the caller. */
    const cands = basis && basis.length >= 2 ? basis : quant;
    clusters = cands.map((c) => {
      const lab = srgbToLab(c.rgb);
      return { ...c, lab, chroma: labChroma(lab) };
    });
  }
  if (clusters.length === 0) return empty;

  /* ⚠ THE SELECTION IS A FUNCTION BECAUSE IT RUNS TWICE (WD 430). Everything from the merge to
     the roles is the same for either basis - the census gate below re-enters it with a different
     candidate list rather than reimplementing any of it. */
  const select = (cand) => {
    // Collapse perceptual near-duplicates, then drop anti-alias blends (colours that
    // sit between two more-dominant ones — pink from red+white, etc.). When the caller
    // keyed out a COLOUR background, feed that colour in as a blend endpoint (kills the
    // orphaned white↔bg greys) and drop any leftover near-bg residue.
    // (Skipped entirely on the anchored path — see the header above.)
    const merged = anchored ? cand : mergeDistinct(cand, o.minDeltaE);
    const anchors = o.removedBg ? [o.removedBg] : [];
    let distinct = anchored ? cand : dropBlends(merged, o.blendTol, o.blendMaxRatio, anchors);
    if (o.removedBg) {
      const bg = o.removedBg;
      const near = (rgb) => Math.sqrt((rgb[0] - bg[0]) ** 2 + (rgb[1] - bg[1]) ** 2 + (rgb[2] - bg[2]) ** 2) <= o.bgResidualTol;
      const kept = distinct.filter((d) => !near(d.rgb));
      if (kept.length) distinct = kept; // never strip everything
    }
    if (!distinct.length) distinct = merged; // safety: never return an empty palette
    const total = distinct.reduce((s, d) => s + d.population, 0) || 1;
    distinct.forEach((d) => (d.fraction = d.population / total));
    const all = distinct.map(toSwatch);

    // --- Edge case: monochrome (all-black / all-white / single-color) ---
    // One distinct color → return it as primary, DON'T fabricate a second.
    if (distinct.length === 1) {
      return { confidence: 'high', primary: toSwatch(distinct[0]), secondary: null, accents: [], all };
    }

    // --- Edge case: photo-like / low-confidence art ---
    // No dominant color AND many significant colors → spread-out muddy art. We
    // refuse to guess a primary/secondary; the caller skips prefill.
    const significant = distinct.filter((d) => d.fraction >= 0.03);
    const noDominant = distinct[0].fraction < o.lowConfidenceDominance;
    const manyClusters = significant.length >= o.lowConfidenceClusterCount;
    if (noDominant && manyClusters) {
      return { confidence: 'low', primary: null, secondary: null, accents: [], all };
    }

    // --- Normal path: pick primary/secondary/accents ---
    const chromatic = distinct.filter((d) => d.chroma >= o.minChroma);
    const preferChromatic = o.ignoreBackground && chromatic.length > 0;
    const candidates = preferChromatic ? chromatic : distinct;

    // Primary = most populous meaningful color (chromatic when available, so a
    // white/black background never wins over a real brand color; a genuinely
    // black/white logo has no chromatic clusters and falls back to population).
    const primary = candidates[0];

    // Secondary = the prominent colour MOST perceptually different from primary (max
    // ΔE), so the fill/outline pair carries the MOST contrast (Ryan: "most possible
    // variance on outlines"). For a gradient that means the two EXTREMES lead (pale-blue
    // + white), with any mid-tone falling to a 3rd accent — not a mid-tone as secondary.
    // Pool: chromatic-only for the raw function (a red-on-white-bg logo shouldn't call
    // background-white a brand colour → secondary null); WD opts in to neutrals
    // (allowNeutralSecondary) because the background is already keyed out, so a prominent
    // white/black is foreground and often the highest-contrast partner.
    let secondary = null;
    let bestDE = 0;
    const secondaryPool = o.allowNeutralSecondary ? distinct : candidates;
    for (const d of secondaryPool) {
      if (d === primary) continue;
      if (d.fraction < o.minColorFraction) continue; // no 1%-share muddy blend as secondary
      const de = deltaE(d.lab, primary.lab);
      if (de >= o.minDeltaE && de > bestDE) {
        bestDE = de;
        secondary = d;
      }
    }

    const chosen = new Set([primary, secondary]);
    const accents = distinct.filter((d) => !chosen.has(d)).map(toSwatch);

    return {
      confidence: 'high',
      primary: toSwatch(primary),
      secondary: secondary ? toSwatch(secondary) : null,
      accents,
      all,
    };
  };

  return select(clusters);
}

/**
 * THE SWATCHES THE MARK DOES NOT ACTUALLY WEAR — the phantom test (WD 430).
 *
 * A swatch CLAIMS a share of the mark (its population, i.e. the pixels the quantizer put in its
 * bucket). Count how many pixels are actually THAT COLOUR, within `tol`. On a real ink the two
 * agree. On the average of a fringe bucket they do not agree at all, because the bucket holds the
 * whole boundary between two inks and only a hairline of it wears the average.
 *
 * @param {Array<{hex:string,rgb:number[],population:number}>} swatches a finished palette's `all`
 * @param {Array<number[]>} pixels the opaque pixels it was built from
 * @param {number} minRatio supported/claimed under this is a phantom (0 judges nothing)
 * @param {number} tol ΔE within which a pixel counts as wearing the swatch
 * @returns {Array<string>} the hexes that claim far more of the mark than wears them
 */
export function unsupportedSwatches(swatches, pixels, minRatio, tol) {
  if (!(minRatio > 0) || !Array.isArray(swatches) || swatches.length < 2 || !pixels || !pixels.length) return [];
  const total = swatches.reduce((acc, c) => acc + (c.population > 0 ? c.population : 0), 0) || 1;
  const labs = swatches.map((c) => srgbToLab(c.rgb));
  const hits = new Array(swatches.length).fill(0);
  for (const px of pixels) {
    const l = srgbToLab(px);
    for (let k = 0; k < labs.length; k++) if (deltaE(l, labs[k]) <= tol) hits[k] += 1;
  }
  return swatches.filter((sw, k) => (hits[k] / pixels.length) < (sw.population / total) * minRatio)
    .map((sw) => sw.hex);
}

/**
 * THE WHOLE SELECTION PIPELINE OVER DECODED PIXELS. Pure — no DOM, so it is node-testable and is
 * the one body `extractPalette` and `tools/census-offline-extract.mjs` both run.
 *
 * ⛔ IT IS ONE BODY ON PURPOSE (WD 430). The offline tool used to carry its own copy of these
 * three passes, with a control that scored it against the app; the control existed because the
 * copy could drift, and a third pass added here would have drifted it on the spot.
 *
 * Three passes, each adjudicating the one before it against the raster:
 *   1. quantize + select              — `analyzePixels`
 *   2. drop RIM ARTIFACTS and re-select — `findRimArtifacts`: colours that exist only as a fringe
 *      AROUND another colour, which value alone cannot distinguish from a real thin colour.
 *   3. ⭐ REBUILD ON THE CENSUS if a surviving swatch is a colour the pixels do not wear.
 *
 * ── ⛔ WHY PASS 3 EXISTS, AND WHY IT IS LAST ──────────────────────────────────────
 * Ryan, on webster's alternative plate: *"that brown color... I don't even see it on the logo
 * itself."* He was right — #754514 is in ZERO pixels of that mark, the median-cut average of its
 * navy and its gold. And it was the SECONDARY ROLE, so one invented colour reached the dealt
 * plates, the band list, the number's mirrored ring and the buyer's own chips.
 *
 * A bucket reports its AVERAGE. That is right for a bucket holding one ink and a lie for a bucket
 * holding the anti-aliased fringe between two, because the average of two inks is a third colour.
 * Measured against the midpoint of the two real tones it sits between, mountmercy's #033C26 lands
 * at ΔE 0.5 — it IS the fringe, exactly — and it is 48.3% of that mark's palette, its single
 * largest swatch.
 *
 * ⛔ THE TEST IS EXISTENCE, NOT GEOMETRY, which is why `dropBlends` and both branches of
 * `findRimArtifacts` miss it: each asks where a colour SITS — on a line, around a host, eroding to
 * nothing — and each labels every pixel to its nearest SURVIVING swatch first. So when the
 * quantizer never gave a real ink a bucket at all, the phantom inherits that ink's whole region and
 * measures as solid as a rock. mountmercy's black got no bucket; its phantom's region IS the black.
 *
 * ⛔ AND IT RUNS ON THE FINISHED PALETTE, NOT INSIDE `analyzePixels`. The first cut of this asked
 * the question at the quantizer and fired on EVERY mark in the corpus, two-colour ones included —
 * because a fringe bucket is exactly what passes 1 and 2 exist to remove, and nearly all of them do
 * get removed. A phantom is the one that SURVIVES them. Asked too early it catches everything,
 * which is the same as catching nothing: `120051002169`, `athletics-edisonohio-edu` and
 * `120150001516` were all rebuilt by that cut and not one of them has a phantom.
 *
 * ⭐ AND THE REPAIR IS NOT A DROP. Dropping the phantom leaves webster gold+white on a mark that is
 * 39% NAVY, CSI gold alone on a mark a quarter BLACK, and mountmercy without either its black or
 * its green — the phantom is a SYMPTOM of buckets misspent, so the ink it stands in for is missing
 * too. The candidates are replaced by measured ones and the selection is re-run.
 */
export function analyzeImageData(imageData, opts = {}) {
  const o = { ...DEFAULTS, ...opts };
  const empty = { confidence: 'low', primary: null, secondary: null, accents: [], all: [] };
  if (!imageData || !imageData.data) return empty;
  const { data } = imageData;
  /* Near-transparent pixels are dropped so a transparent PNG's empty field never pollutes the
     palette. (Boundary/anti-alias blends are handled by the passes below, NOT by a spatial filter —
     a spatial filter can't tell a 1px anti-alias seam from a 1px intentional outline, so it eats
     real thin colours.) */
  let pixels = [];
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < o.alphaThreshold) continue;
    pixels.push([data[i], data[i + 1], data[i + 2]]);
  }
  if (!pixels.length) return empty; // fully transparent — nothing to extract; don't guess

  const first = analyzePixels(pixels, o);
  let res = first;
  /* PASS 2 (Ryan 2026-07-25): the flat pixel list has no geometry, so a colour that exists only as
     a fringe around another one is indistinguishable from a real thin colour by value alone. Now
     that there is a candidate palette AND the ImageData, ask which candidates are rim artifacts,
     drop their pixels, and re-analyse. Re-analysing rather than filtering the RESULT is what keeps
     the roles honest — removing a colour changes which survivor is primary and every share below
     it. ⚠ The surviving pixel list is carried forward: pass 3 must not re-measure a census over
     pixels this pass has already ruled out. */
  const cand = Array.isArray(first.all) ? first.all : [];
  /* ⛔ NEVER DOWN TO ONE COLOUR ON A DECLARED-INK SOURCE (WD 470). A vector's stated inks are the
     artwork's own list, and a thin OUTLINE in one of them looks exactly like a rim: bctornados' white
     stroke and hiuroyals' white keyline were both judged fringe and deleted, leaving a ONE-swatch
     palette, so `extractLogoStructure` (which needs two) answered low and the weight number never
     learned the logo has an outline — his flags, pass 9.
       Narrow on purpose: skipping the rim pass on every declared-ink mark was netted over all 29
     vector marks in the corpus and moved 8 palettes (abbey, a named control, among them); this
     clause moves only the two that collapse to one colour. */
  let artifacts = cand.length > 1 ? findRimArtifacts(imageData, cand, o) : [];
  const declared = Array.isArray(o.paletteAnchors) && o.paletteAnchors.length && !globalThis.__wdRimOnDeclared;
  if (declared && cand.length - artifacts.length < 2) artifacts = [];
  if (artifacts.length) {
    const bad = new Set(artifacts);
    const rgbs = cand.map((c) => c.rgb);
    const kept = [];
    for (const px of pixels) {
      let best = 0, bestD = Infinity;
      for (let k = 0; k < rgbs.length; k++) {
        const t = rgbs[k];
        const d = (px[0] - t[0]) ** 2 + (px[1] - t[1]) ** 2 + (px[2] - t[2]) ** 2;
        if (d < bestD) { bestD = d; best = k; }
      }
      if (!bad.has(best)) kept.push(px);
    }
    if (kept.length) { pixels = kept; res = analyzePixels(pixels, o); }
  }

  /* ⛔ A DECLARED-INK SOURCE IS NOT JUDGED BY THE CENSUS. When the caller hands in
     `paletteAnchors` the palette is the ARTWORK'S OWN list, assigned rather than discovered, and a
     bin average has no standing to overrule it - snapping a vector's stated #FEFEFE to a measured
     #FDFDFE would be this gate doing damage for no gain. That path also already has its own
     corroboration test, and a better one: under 70% pixel coverage it abandons the anchors and
     falls back to discovery, which is the same question (do the declared colours exist?) answered
     by counting pixels rather than by ΔE.
     ⚠ KNOWN GAP: a mark that FELL BACK to discovery is quantized like any other and could carry a
     phantom, and this skips it anyway because the anchors were supplied. It is the safe side of the
     trade and the corpus has 29 vector marks; revisit with a measured count, not a hunch. */
  if (Array.isArray(o.paletteAnchors) && o.paletteAnchors.length) return res;
  if (!(o.phantomMinSupport > 0)) return res;
  const flagged = new Set(unsupportedSwatches(res.all || [], pixels, o.phantomMinSupport, o.phantomSupportTol));
  if (!flagged.size) return res;
  // Does any flagged swatch reach a surface? See `phantomMinShare`.
  const totalPop = (res.all || []).reduce((acc, c) => acc + (c.population > 0 ? c.population : 0), 0) || 1;
  const roleHex = new Set([res.primary, res.secondary].filter(Boolean).map((r) => r.hex));
  const seen = (res.all || []).some((sw) => flagged.has(sw.hex)
    && (roleHex.has(sw.hex) || sw.population / totalPop >= o.phantomMinShare));
  if (!seen) return res;
  /* ⚠ Once ANY phantom is worth repairing, ALL of them are replaced - the rebuild re-measures every
     population by assignment, so leaving a known-invented swatch in the basis would hand it a share
     of the mark all over again. The share bar decides whether to act, not what to keep. */
  const bad = flagged;
  /* The census names the REPLACEMENT. The test above needs no census - it asks the pixels directly
     - but the repair does: it has to put the ink the phantom was standing in for back on the list,
     and that ink is by definition one the quantizer never gave a bucket to. */
  const census = dropSeamTones(censusTones(pixels));
  /* ── ⭐ THE REPAIR IS SURGICAL: KEEP WHAT THE PIXELS CORROBORATE ───────────────────────
   * ⛔ THE FIRST CUT REPLACED THE WHOLE PALETTE WITH THE CENSUS AND THAT LOST REAL COLOURS.
   * `angry-panda` went from seven swatches to five, and the two it lost were its navy #354C93 and
   * its cyan #23A3C1 - both plainly in the artwork. The cause is a difference in what the two
   * lists are RANKED by: the census is ordered by population and truncated, while median cut
   * splits by colour SPREAD, which is exactly how a small but chromatically distinct colour earns
   * a bucket. Handing the whole job to the census throws that away on every rich mark to fix a
   * defect in one or two of its swatches.
   *
   * So the corroborated swatches are KEPT, as they were spelled, and only the phantoms are
   * replaced - by the census tones that nothing kept already represents, which is the ink the
   * phantom was standing in for.
   *
   * ⚠ AND THE POPULATIONS ARE RE-MEASURED, NOT CARRIED OVER. A kept swatch's count was taken
   * while the phantom was absorbing pixels: webster's brown was reported at 8.5% of a palette that
   * had no navy in it at all, and every share below it was wrong by the same token. Shares are
   * load-bearing (`shownLogoTones`' 3% accent bar, the plate deal's read-share guard), so every
   * pixel is re-assigned to the nearest tone of the repaired list - the same thing the
   * declared-ink path does, for the same reason. */
  const keep = (res.all || []).filter((sw) => !bad.has(sw.hex)).map((sw) => ({ hex: sw.hex, rgb: sw.rgb }));
  const klabs = keep.map((k) => srgbToLab(k.rgb));
  const add = census.filter((t) => {
    const lab = srgbToLab(t.rgb);
    return !klabs.some((k) => deltaE(lab, k) < o.minDeltaE);
  }).map((t) => ({ hex: t.hex, rgb: t.rgb }));
  const tones = [...keep, ...add];
  if (tones.length < 2) return res;
  const tlabs = tones.map((t) => srgbToLab(t.rgb));
  const pop = new Array(tones.length).fill(0);
  for (const px of pixels) {
    const lab = srgbToLab(px);
    let best = 0, bestD = Infinity;
    for (let k = 0; k < tlabs.length; k++) {
      const d = deltaE(lab, tlabs[k]);
      if (d < bestD) { bestD = d; best = k; }
    }
    pop[best] += 1;
  }
  const basis = tones.map((t, k) => ({ ...t, population: pop[k] }))
    .filter((t) => t.population >= pixels.length * 0.002) // a tone nothing lands on is not a tone
    .sort((a, b) => b.population - a.population)
    .slice(0, o.maxColors);
  if (basis.length < 2) return res;
  const rebuilt = analyzePixels(pixels, o, basis);
  /* ⚠ Never trade a confident palette for an unconfident one. The census is evidence about which
     TONES exist; it is not a reason to stop answering. */
  return rebuilt.confidence === 'high' || res.confidence !== 'high' ? rebuilt : res;
}

/**
 * THE SWATCHES THAT EXIST ONLY AS A SMEAR — the anti-aliased / JPEG-rung edge between two other
 * colours, read as a colour of its own (WD 481).
 *
 * Grand Hyatt is one red. Its 35 lb plate was dealt #A43B60, a maroon no part of the logo is printed
 * in: the JPEG's edge, red darkening into the white backdrop, 5% of the 128px palette. Four tests on
 * the 128px read were measured dead (share, edge-only, band width, red-white blend), because at that
 * size a smear and a real thin outline are the same one pixel. At full size they are not:
 *   • a smear is a PASSING value. Each of its pixels sits on the straight line between the colours
 *     either side of it (a RAMP), and it has no inside: a 1-px erosion leaves nothing.
 *   • a real thin line is a PLATEAU. Its pixels repeat across the line, and where it is narrow it is
 *     still not between its neighbours - a keyline is contrasting by definition.
 * Both tests, at full size, on art with at least `smearMinScale` x the 128 canvas to give. ⚠ At 2x
 * (300px marks) Hawaii Royals' grey edge fired, and at that size a thin silver keyline and a
 * blue-to-white smear are the same 1 px, so the bar is 3x: Baldwin Wallace's dark gold (324px) is a
 * smear by eye and is left alone with it. The same read catches Vikings' dark brown (its red meeting
 * its black), a smear that had been dealt as a plate too.
 *
 * Pure (ImageData in, hexes out). The PRIMARY is never a smear; the caller decides what to do with
 * the answer - today only `plateDealDeck` reads it, so the logo, its chips and its outlines are
 * untouched.
 *
 * @param {ImageData} imageData the art at full size (capped at `smearEdge`)
 * @param {{hex:string,rgb:number[]}[]} swatches the palette's `all`
 * @param {object} [opts] DEFAULTS overrides; `removedBg` is the backdrop a transparent pixel stands for
 * @returns {string[]} lower-case hexes, as the palette spells them
 */
export function smearSwatches(imageData, swatches, opts = {}) {
  const o = { ...DEFAULTS, ...opts };
  const list = (swatches || []).filter((s) => s && Array.isArray(s.rgb));
  if (list.length < 2 || !imageData) return [];
  const { width: W, height: H, data } = imageData;
  if (Math.max(W, H) < o.smearMinScale * o.maxEdge) return [];
  const bg = Array.isArray(o.removedBg) ? o.removedBg : [255, 255, 255];
  const labs = list.map((s) => srgbToLab(s.rgb));
  const N = W * H;
  const rgb = new Float32Array(N * 3);   // composited over the backdrop, for the ramp test
  const lbl = new Int16Array(N).fill(-1); // nearest swatch, opaque pixels only
  const cache = new Map();
  for (let i = 0; i < N; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2], a = data[i * 4 + 3];
    const t = a / 255;
    rgb[i * 3] = r * t + bg[0] * (1 - t);
    rgb[i * 3 + 1] = g * t + bg[1] * (1 - t);
    rgb[i * 3 + 2] = b * t + bg[2] * (1 - t);
    if (a < o.alphaThreshold) continue;
    const key = (r << 16) | (g << 8) | b;
    let k = cache.get(key);
    if (k === undefined) {
      const l = srgbToLab([r, g, b]);
      k = 0;
      for (let j = 1; j < labs.length; j++) if (deltaE(l, labs[j]) < deltaE(l, labs[k])) k = j;
      cache.set(key, k);
    }
    lbl[i] = k;
  }
  const GAP = 60, END = 15, TOL = 30; // RGB: endpoints apart, pixel off both ends, pixel on the line
  const px = (x, y, c) => rgb[(Math.min(H - 1, Math.max(0, y)) * W + Math.min(W - 1, Math.max(0, x))) * 3 + c];
  const count = new Array(list.length).fill(0);
  const onRamp = new Array(list.length).fill(0);
  const solid = new Array(list.length).fill(0);
  const DIRS = [[1, 0], [0, 1], [1, 1], [1, -1]];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      const k = lbl[i];
      if (k < 0) continue;
      count[k] += 1;
      let inside = true;
      for (let dy = -1; dy <= 1 && inside; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx, yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= W || yy >= H || lbl[yy * W + xx] !== k) { inside = false; break; }
        }
      }
      if (inside) solid[k] += 1;
      const p0 = rgb[i * 3], p1 = rgb[i * 3 + 1], p2 = rgb[i * 3 + 2];
      let ramp = false;
      for (const [dx, dy] of DIRS) {
        for (let st = 1; st <= 2 && !ramp; st++) {
          const a0 = px(x - dx * st, y - dy * st, 0), a1 = px(x - dx * st, y - dy * st, 1), a2 = px(x - dx * st, y - dy * st, 2);
          const b0 = px(x + dx * st, y + dy * st, 0), b1 = px(x + dx * st, y + dy * st, 1), b2 = px(x + dx * st, y + dy * st, 2);
          const e0 = b0 - a0, e1 = b1 - a1, e2 = b2 - a2;
          const n2 = e0 * e0 + e1 * e1 + e2 * e2;
          if (n2 < GAP * GAP) continue;
          if (Math.hypot(p0 - a0, p1 - a1, p2 - a2) < END || Math.hypot(p0 - b0, p1 - b1, p2 - b2) < END) continue;
          const t = Math.max(0, Math.min(1, ((p0 - a0) * e0 + (p1 - a1) * e1 + (p2 - a2) * e2) / n2));
          if (Math.hypot(p0 - (a0 + t * e0), p1 - (a1 + t * e1), p2 - (a2 + t * e2)) <= TOL) ramp = true;
        }
        if (ramp) break;
      }
      if (ramp) onRamp[k] += 1;
    }
  }
  return list
    .filter((s, k) => k > 0 && count[k] > 0
      && onRamp[k] / count[k] >= o.smearRampMin && solid[k] / count[k] <= o.smearSolidMax)
    .map((s) => s.hex);
}

// ===========================================================================
// BROWSER-ONLY: decode + downscale + getImageData wrapper
// ===========================================================================

/** Create a 2D context of size w×h (OffscreenCanvas when available). */
function makeCanvas(w, h) {
  if (typeof OffscreenCanvas !== 'undefined') {
    const c = new OffscreenCanvas(w, h);
    return { canvas: c, ctx: c.getContext('2d', { willReadFrequently: true }) };
  }
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return { canvas: c, ctx: c.getContext('2d', { willReadFrequently: true }) };
}

/** Natural dimensions of a decodable source. */
function sourceDimensions(src) {
  const w = src.naturalWidth || src.width || src.videoWidth || 0;
  const h = src.naturalHeight || src.height || src.videoHeight || 0;
  return [w, h];
}

/**
 * Decode `imageSource` if needed, draw it downscaled onto a small analysis
 * canvas, and return its ImageData. The downscale is analysis-only — the
 * caller's image is never mutated.
 *
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap|Blob|File|ImageData} imageSource
 * @param {number} maxEdge
 * @returns {Promise<ImageData>}
 */
async function toImageData(imageSource, maxEdge) {
  // Already pixels — used straight through (also handy for tests/callers).
  if (typeof ImageData !== 'undefined' && imageSource instanceof ImageData) {
    return imageSource;
  }

  let drawable = imageSource;
  let createdBitmap = null;

  // Blob/File (and anything createImageBitmap accepts) → decode to a bitmap.
  // This is where HEIC / corrupt files throw and are caught by the caller.
  const isBlob = typeof Blob !== 'undefined' && imageSource instanceof Blob;
  if (isBlob) {
    createdBitmap = await createImageBitmap(imageSource);
    drawable = createdBitmap;
  }

  const [sw, sh] = sourceDimensions(drawable);
  if (!sw || !sh) throw new Error('palette-extract: image source has zero dimensions');

  // Compute analysis size — never upscale; cap the longest edge at maxEdge.
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const dw = Math.max(1, Math.round(sw * scale));
  const dh = Math.max(1, Math.round(sh * scale));

  const { ctx } = makeCanvas(dw, dh);
  if (!ctx) throw new Error('palette-extract: could not acquire 2D context');
  // Nearest-neighbour downscale (smoothing OFF): a smoothed shrink BLENDS colour
  // boundaries into muddy intermediates (teal+white→grey, rust+white→tan), which
  // then quantize into spurious "extra" swatches that don't exist in the art. We
  // want ACTUAL logo pixels, so the palette is the real brand colours, not blends.
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(drawable, 0, 0, dw, dh);
  const data = ctx.getImageData(0, 0, dw, dh);

  if (createdBitmap && createdBitmap.close) createdBitmap.close();
  return data;
}

/**
 * Extract a brand palette from a logo image. Public entry point.
 *
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap|Blob|File|ImageData} imageSource
 * @param {Partial<typeof DEFAULTS>} [opts]
 * @returns {Promise<PaletteResult>} resolves (never rejects) — decode failures
 *          come back as { confidence:'low', primary:null, secondary:null,
 *          all:[], error } so the caller can show a friendly message.
 */
export async function extractPalette(imageSource, opts = {}) {
  const o = { ...DEFAULTS, ...opts };
  try {
    /* ⛔ THE SELECTION IS `analyzeImageData` AND NOT A SECOND COPY OF IT (WD 430). Only the DECODE
       is browser-only; everything after it was inlined here and re-implemented a second time in
       `tools/census-offline-extract.mjs`, which carried a control scoring itself against the app
       precisely because the copy could drift. Adding a third pass would have drifted it that day.
       One body, two callers, and the offline control is now true by construction. */
    const res = analyzeImageData(await toImageData(imageSource, o.maxEdge), o);
    // The smear read is a second look at full size; it never touches the palette itself, and if it
    // fails the palette stands without it (no field = nothing is a smear).
    if (o.smearEdge > 0 && res.confidence === 'high' && Array.isArray(res.all) && res.all.length > 1) {
      try {
        res.smearHexes = smearSwatches(await toImageData(imageSource, o.smearEdge), res.all, o);
      } catch { /* keep the palette */ }
    }
    return res;
  } catch (error) {
    // HEIC / decode failures / unsupported sources: degrade gracefully instead
    // of throwing. Caller shows "Couldn't read that image — try a PNG/JPG."
    return { confidence: 'low', primary: null, secondary: null, accents: [], all: [], error };
  }
}

export default extractPalette;
